import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Fade,
  Menu,
  MenuItem,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../v2/general/CustomDataGrid';
import type { VirtualElement } from '@popperjs/core';
import CustomButton from '../v2/general/CustomButton/CustomButton';
import CustomColumnMenu, {
  columnMenuPopperSx,
} from '../v2/general/CustomDataGrid/CustomColumnMenu';
import { useAnimatedDensity } from '../v2/general/CustomDataGrid/useAnimatedDensity';
import { useColumnOrder } from './useColumnOrder';
import servicesMock from '../../data/mockedData/schedules/services.json';
import { useModal } from '../../store/ModalProvider';
import type {
  ServiceDetailModalData,
  ServiceItineraryModalData,
  SubcontractDriversModalData,
} from '../../types/modal';
import { useNavigate } from 'react-router-dom';

const serviceMenuItems = [
  'Ver Itinerario do Servico',
  'Ver Detalhe do Servico',
  'Abrir Requisicao',
];

interface Service {
  id: number;
  startDate: string;
  startTime: string;
  service: string;
  startLocation: string;
  endLocation: string;
  endTime: string;
  endDate: string;
  client: string;
  observations: string;
  driver: string;
  vehicle: string;
}

const ServicesToSchedule = React.memo(function ServicesToSchedule() {
  const [density, setDensity] = useState<DensityMode>('standard');
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const { openModal } = useModal();
  const navigate = useNavigate();
  
  // Use our custom animation hook
  const { animateRows, triggerRowAnimations } = useDataGridAnimations();

  const services = useMemo(() => servicesMock as Service[], []);
  const servicesCount = services.length;
  const selectedCount = selectionModel.length;
  const hasSelection = selectedCount > 0;
  const rowById = useMemo(
    () => new Map(services.map((row) => [row.id, row])),
    [services],
  );
  const allRowIds = useMemo(() => services.map((row) => row.id), [services]);
  const isAllSelected =
    allRowIds.length > 0 && selectionModel.length === allRowIds.length;
  const isIndeterminate =
    selectionModel.length > 0 && selectionModel.length < allRowIds.length;

  const getRowHighlightClass = (row: Service) =>
    row.observations?.trim() ? 'row--note' : '';

  const tooltipAnchor = useMemo<VirtualElement | null>(() => {
    if (!tooltipPosition) {
      return null;
    }

    return {
      getBoundingClientRect: () => ({
        x: tooltipPosition.x,
        y: tooltipPosition.y,
        top: tooltipPosition.y,
        left: tooltipPosition.x,
        right: tooltipPosition.x,
        bottom: tooltipPosition.y,
        width: 0,
        height: 0,
        toJSON: () => {},
      }),
    };
  }, [tooltipPosition]);

  const getRowIdFromCell = (cell: HTMLElement) => {
    const rowElement = cell.closest('.MuiDataGrid-row');
    const rowId = rowElement?.getAttribute('data-id');
    if (!rowId) {
      return null;
    }
    const parsedId = Number(rowId);
    return Number.isNaN(parsedId) ? null : parsedId;
  };

  const handleDensityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDensity: DensityMode | null,
  ) => {
    if (newDensity) {
      setDensity(newDensity);
    }
  };

  // 🎬 JAVASCRIPT-BASED ANIMATION - Animate the actual DataGrid rowHeight prop!
  const cellPadding = density === 'compact' ? '2px 4px' : '6px 4px';
  
  // Calculate target heights for animation - compact design
  const targetRowHeight = density === 'compact' ? 28 : 38; // Standard height updated to 38px
  
  // 🎬 USE JAVASCRIPT ANIMATION HOOK - This will smoothly animate the rowHeight value!
  const { rowHeight } = useAnimatedDensity(targetRowHeight, targetRowHeight);
  
  console.log('🎬 JS Animation - Density:', density, 'Target:', targetRowHeight, 'Animated:', rowHeight.toFixed(1));

  const handleOpenService = useCallback(
    (serviceId: number) => {
      navigate(`/schedules/service/${serviceId}`);
    },
    [navigate],
  );

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    rowId: number,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const buildItineraryModalData = (row?: Service): ServiceItineraryModalData => ({
    serviceId: row?.id ?? '0',
    serviceType: 'Agência de Viagens',
    requestCode: row?.service ?? '1115_PE2_PNE2',
    startDateTime:
      row?.startDate && row?.startTime
        ? `${row.startDate} ${row.startTime}`
        : undefined,
    endDateTime:
      row?.endDate && row?.endTime ? `${row.endDate} ${row.endTime}` : undefined,
    observations: row?.observations,
    client: {
      name: row?.client,
    },
    itinerary: {
      description: row
        ? `${row.startLocation ?? ''} - ${row.endLocation ?? ''}`.trim()
        : undefined,
    },
  });

  const buildServiceDetailModalData = (
    row?: Service,
  ): ServiceDetailModalData => ({
    serviceId: row?.id ?? '0',
    startCommercial:
      row?.startDate && row?.startTime
        ? `${row.startDate} ${row.startTime}`
        : undefined,
    startSchedule:
      row?.startDate && row?.startTime
        ? `${row.startDate} ${row.startTime}`
        : undefined,
    startLocation: row?.startLocation,
    endCommercial:
      row?.endDate && row?.endTime ? `${row.endDate} ${row.endTime}` : undefined,
    endSchedule:
      row?.endDate && row?.endTime ? `${row.endDate} ${row.endTime}` : undefined,
    endLocation: row?.endLocation,
    vehicle: row?.vehicle,
    serviceCode: row?.service,
    requestCode: row?.service,
    observations: row?.observations,
    driver: {
      name: row?.driver,
    },
  });

  const handleMenuAction = (action: string) => {
    if (action === 'Ver Itinerario do Servico') {
      const row = menuRowId !== null ? rowById.get(menuRowId) : undefined;
      openModal('Requisicao do Servico', buildItineraryModalData(row), []);
      handleCloseMenu();
      return;
    }
    if (action === 'Ver Detalhe do Servico') {
      const row = menuRowId !== null ? rowById.get(menuRowId) : undefined;
      openModal('Detalhe do Servico', buildServiceDetailModalData(row), []);
      handleCloseMenu();
      return;
    }
    console.log('Servico menu:', action, menuRowId);
    handleCloseMenu();
  };

  const isTooltipBlockedField = (field?: string) =>
    field === 'startDate' || field === 'actions';

  const handleCellMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const cell = event.currentTarget as HTMLElement;
    const field = cell.dataset.field;
    if (isTooltipBlockedField(field)) {
      setTooltipPosition(null);
      return;
    }

    const rowId = getRowIdFromCell(cell);
    if (rowId === null) {
      return;
    }

    const row = rowById.get(rowId);
    if (!row || !getRowHighlightClass(row)) {
      setTooltipPosition(null);
      return;
    }

    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleCellMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const cell = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget) {
      const currentRow = cell.closest('.MuiDataGrid-row');
      const nextRow = relatedTarget.closest?.('.MuiDataGrid-row');
      if (
        currentRow &&
        nextRow &&
        currentRow.getAttribute('data-id') ===
          nextRow.getAttribute('data-id')
      ) {
        return;
      }
    }
    setTooltipPosition(null);
  };

  const handleToggleAll = (checked: boolean) => {
    setSelectionModel(checked ? allRowIds : []);
  };

  const handleToggleRow = (rowId: number) => {
    setSelectionModel((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const isInteractiveTarget = (target: HTMLElement | null) =>
    Boolean(
      target?.closest(
        'button, a, .MuiCheckbox-root, input[type="checkbox"], .row-actions',
      ),
    );

  const getRowClickField = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    return target?.closest('[data-field]')?.getAttribute('data-field');
  };

  const handleRowClick = (
    params: GridRowParams<Service>,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    const field = getRowClickField(event);
    if (field === 'actions') {
      return;
    }
    if (isInteractiveTarget(event.target as HTMLElement)) {
      return;
    }
    handleOpenService(Number(params.id));
  };

  const handleSchedule = () => {
    console.log('Escalar serviços:', selectionModel);
  };

  const handleSubcontract = useCallback(() => {
    const selectedServiceIds = selectionModel
      .map((id) =>
        typeof id === 'number' || typeof id === 'string' ? id : String(id),
      )
      .filter((id) => id !== '');

    const subcontractData: SubcontractDriversModalData = {
      services,
      selectedServiceIds,
      email: 'email@email.com',
      contact: '910000000',
      comments:
        'Tempus in habitasse condimentum orci massa urna phasellus bibendum. Adipiscing in platea vitae est volutpat adipiscing quam.',
      smsConfirmationRequest: true,
      sendCopyToMe: true,
    };

    openModal('Subcontratar Motoristas', subcontractData, []);
  }, [openModal, selectionModel, services]);

  const baseColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'startDate',
        headerName: 'Data Início',
        width: 124,
        sortable: false,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              size='small'
              color='primary'
              onChange={(event) => handleToggleAll(event.target.checked)}
            />
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '12px',
                color: '#8695AF',
                letterSpacing: '0.17px',
                lineHeight: '24px',
              }}
            >
              Data Início
            </Typography>
          </Box>
        ),
        renderCell: (params) => {
          const rowId = Number(params.id);
          const isChecked = selectionModel.includes(rowId);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                checked={isChecked}
                size='small'
                color='primary'
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleToggleRow(rowId)}
              />
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  lineHeight: 1.3,
                  color: '#092056',
                }}
              >
                {params.value}
              </Typography>
            </Box>
          );
        },
      },
      { field: 'startTime', headerName: 'Hora Início', width: 90 },
      { field: 'service', headerName: 'Serviço', width: 57 },
      {
        field: 'startLocation',
        headerName: 'Local Início',
        width: 130,
      },
      {
        field: 'endLocation',
        headerName: 'Local Término',
        width: 130,
      },
      { field: 'endTime', headerName: 'Hora Término', width: 89 },
      { field: 'endDate', headerName: 'Data Término', width: 89 },
      { field: 'client', headerName: 'Cliente', width: 130 },
      {
        field: 'observations',
        headerName: 'Observações',
        width: 130,
        sortable: false,
      },
      { field: 'driver', headerName: 'Motorista', width: 107 },
      { field: 'vehicle', headerName: 'Viatura', width: 51 },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 50,
        cellClassName: 'actions-cell',
        renderCell: (params) => (
          <Box
            className='row-actions'
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              opacity: menuRowId === Number(params.id) ? 1 : undefined,
              pointerEvents:
                menuRowId === Number(params.id) ? 'auto' : undefined,
            }}
          >
            <IconButton
              size='small'
              onClick={(event) => handleOpenMenu(event, Number(params.id))}
              sx={{ padding: 0, width: 24, height: 24 }}
            >
              <MoreVertIcon sx={{ fontSize: 14, color: '#00A5FF' }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [
      handleOpenMenu,
      handleOpenService,
      handleToggleAll,
      handleToggleRow,
      isAllSelected,
      isIndeterminate,
      menuRowId,
      selectionModel,
    ],
  );
  const { orderedColumns, handleColumnOrderChange } =
    useColumnOrder(baseColumns);

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '10px',
        padding: '24px 16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '16px',
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: 1.334,
                color: '#092056',
              }}
            >
              {servicesCount} Por Escalar
            </Typography>
            <Fade in={hasSelection} timeout={200} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    width: '1px',
                    height: '24px',
                    backgroundColor: '#8695AF',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: 1.3,
                    color: '#8695AF',
                  }}
                >
                  {selectedCount} selecionado(s):
                </Typography>
              </Box>
            </Fade>
          </Box>
          <Collapse in={hasSelection} timeout={200} mountOnEnter unmountOnExit>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CustomButton
                variant='text'
                onClick={handleSchedule}
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#00A5FF',
                  textTransform: 'none',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Escalar
              </CustomButton>
              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: '#8695AF',
                }}
              />
              <CustomButton
                variant='text'
                onClick={handleSubcontract}
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#00A5FF',
                  textTransform: 'none',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Subcontratar
              </CustomButton>
            </Box>
          </Collapse>
        </Box>
        <ToggleButtonGroup
          value={density}
          exclusive         
          size='small'
          sx={{
            border: '1px solid rgba(134, 149, 175, 0.15)',
            borderRadius: '4px',
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: 0,
              padding: '4.8px',
              '&:not(:last-child)': {
                borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
              },
              '&.Mui-selected': {
                backgroundColor: '#f5f7fc',
                '&:hover': {
                  backgroundColor: '#f5f7fc',
                },
              },
            },
          }}
        >
          <ToggleButton value='compact' aria-label='densidade compacta'>
            <TableRowsIcon
              sx={{
                width: '14.4px',
                height: '14.4px',
                color:
                  density === 'compact'
                    ? '#00A5FF'
                    : 'rgba(134, 149, 175, 1)',
              }}
            />
          </ToggleButton>
          <ToggleButton value='standard' aria-label='densidade espaçada'>
            <ViewHeadlineIcon
              sx={{
                width: '14.4px',
                height: '14.4px',
                color:
                  density === 'standard'
                    ? '#00A5FF'
                    : 'rgba(134, 149, 175, 1)',
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPro
          rows={services}
          columns={orderedColumns}
          onColumnOrderChange={handleColumnOrderChange}
          rowHeight={rowHeight}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          hideFooter
          getRowClassName={(params) => getRowHighlightClass(params.row)}
          slots={{ columnMenu: CustomColumnMenu }}
          slotProps={{
            cell: {
              onMouseEnter: handleCellMouseEnter,
              onMouseLeave: handleCellMouseLeave,
            },
            basePopper: {
              sx: columnMenuPopperSx,
            },
          }}
          sx={[
            {
              '--density-row-height': `${rowHeight}px`,
              border: 'none',
              '--DataGrid-rowBorderColor': 'transparent',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
                borderTop: 'none',
                boxShadow: 'none',
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                padding: cellPadding,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '12px',
                color: '#8695AF',
                letterSpacing: '0.17px',
                lineHeight: '24px',
                textTransform: 'none',
              },
              '& .MuiDataGrid-columnSeparator': {
                opacity: 0,
                color: 'rgba(134, 149, 175, 0.6)',
                transition: 'opacity 0.2s ease',
              },
              '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
                opacity: 1,
              },
              '& .MuiDataGrid-columnSeparator--resizable': {
                cursor: 'col-resize',
              },
              '& .MuiDataGrid-columnHeader': {
                borderRight: 'none',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'Poppins',
                fontSize: '12px',
                color: '#092056',
                lineHeight: 1.3,
                borderBottom: 'none',
                borderRight: 'none',
              },
              '& .MuiDataGrid-cell.actions-cell': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              },
              '& .MuiDataGrid-row': {
                borderBottom: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 165, 255, 0.06)',
              },
              '& .row-actions': {
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease',
              },
              '& .MuiDataGrid-row:hover .row-actions': {
                opacity: 1,
                pointerEvents: 'auto',
              },
              '& .row--note': {
                backgroundColor: 'rgba(0, 165, 255, 0.06)',
              },
            },
          ]}
        />
      </Box>
      <Tooltip
        open={Boolean(tooltipAnchor)}
        placement='bottom-start'
        title={
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '10px',
              lineHeight: '14px',
              color: '#092056',
              whiteSpace: 'nowrap',
            }}
          >
            Confirme as Observações
          </Typography>
        }
        PopperProps={{
          anchorEl: tooltipAnchor,
        }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#FFFFFF',
              borderRadius: '4px',
              pointerEvents: 'none',
              padding: '10px',
              boxShadow:
                '0px 1px 10px 0px rgba(9, 32, 86, 0.12), 0px 4px 5px 0px rgba(9, 32, 86, 0.14), 0px 2px 4px -1px rgba(9, 32, 86, 0.2)',
              maxWidth: 'none',
            },
          },
        }}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <Box sx={{ width: 0, height: 0 }} />
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow:
              '0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
            mt: '4px',
            minWidth: '236px',
          },
        }}
        MenuListProps={{
          sx: {
            padding: '4px 0',
          },
        }}
      >
        {serviceMenuItems.map((item) => (
          <MenuItem
            key={item}
            onClick={() => handleMenuAction(item)}
            sx={{
              padding: '8px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: 1.5,
                color: '#092056',
                flex: 1,
              }}
            >
              {item}
            </Typography>
            <KeyboardArrowRightRoundedIcon
              sx={{ fontSize: 18, color: '#00A5FF' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
});

export default ServicesToSchedule;





